import { WeddingState } from "./features/weddingSlice";

const STORAGE_KEY = "wedding_state";
const TTL_MS = 12 * 60 * 60 * 1000;

type PersistedWeddingState = {
  savedAt: unknown;
  state: unknown;
};

const isPersistedWeddingState = (value: unknown): value is PersistedWeddingState => {
  if (!value || typeof value !== "object") return false;
  return "savedAt" in value && "state" in value;
};

const isValidSavedAt = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);
const isExpired = (savedAt: number) => Date.now() - savedAt > TTL_MS;

export const clearWeddingState = () => {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error("Failed to clear persisted wedding state:", error);
  }
};

export const loadWeddingState = (): WeddingState | undefined => {
  if (typeof window === "undefined") return undefined;

  try {
    const serializedState = window.localStorage.getItem(STORAGE_KEY);
    if (!serializedState) return undefined;

    const parsed = JSON.parse(serializedState);
    if (!parsed || typeof parsed !== "object") return undefined;

    if (isPersistedWeddingState(parsed)) {
      const savedAt = parsed.savedAt;
      if (!isValidSavedAt(savedAt) || isExpired(savedAt)) {
        clearWeddingState();
        return undefined;
      }

      if (!parsed.state || typeof parsed.state !== "object") {
        clearWeddingState();
        return undefined;
      }

      const { hydrationComplete, ...persisted } = parsed.state as WeddingState;
      return persisted as WeddingState;
    }

    const { hydrationComplete, ...persisted } = parsed as WeddingState;
    return persisted as WeddingState;
  } catch (error) {
    console.error("Failed to load persisted wedding state:", error);
    return undefined;
  }
};

export const saveWeddingState = (state: any) => {
  if (typeof window === "undefined") return;

  try {
    const weddingState = state.wedding ?? {};
    if (!weddingState.hydrationComplete) return;
    const { hydrationComplete, ...persisted } = weddingState;
    const serializedState = JSON.stringify({
      savedAt: Date.now(),
      state: persisted
    });
    window.localStorage.setItem(STORAGE_KEY, serializedState);
  } catch (error) {
    console.error("Failed to persist wedding state:", error);
  }
};
